import { useIntl } from "react-intl";

import FeatureGates from "@atlassian/feature-gate-js-client";

import BannerAlert, { BannerProps } from "@/components/wpl/banner-alert/src";
import { ToastComponent } from "@/components/wpl/toast/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import GlobalFooter from "@/patterns/global-footer/src/index";
import GlobalNavigation from "@/patterns/global-nav/src/index";

import { JiraSoftwarePageProps } from "../../../../pages/jira-software-page/jira-software-page";
import { ParentScrollTracking } from "../../../../utils/scroll-tracking";
import {
    BlogsSection,
    CTASection,
    DevOpsSection,
    LoggedInHeroSection,
    LoggedOutHeroSection,
    IntegrationSection,
    JiraSoftwareNav,
    ScalingSection,
    TabsSection,
    WorkflowSection
} from "../sections/productionized";
import { filterAvailableJiraSites } from "../sections/productionized/logged-in-hero/logged-in-hero";
import Team24AdBannerSection from "../sections/productionized/team24-ad-banner";

export const JiraProductTourOverview = ({
    availableProducts,
    userProfile
}: JiraSoftwarePageProps) => {
    const intl = useIntl();
    const availableJiraSites = filterAvailableJiraSites(availableProducts);

    const showAiBanner = FeatureGates.getExperimentValue(
        "wac-ai-banner-jira",
        "ShowButterBar",
        true
    );
    const aiBannerMessages: BannerProps = {
        identifier: "jira-software.banner-alert.atlassian-intelligence",
        text: intl.formatMessage({
            id: "jira-software.banner-alert.text",
            defaultMessage:
                "Transform teamwork with Atlassian Intelligence - now available."
        }),
        linkText: intl.formatMessage({
            id: "jira-software.banner-alert.link-text",
            defaultMessage: "Learn more"
        }),
        linkUrl: useLocalizedHref("/software/artificial-intelligence"),
        dataEventProps: {
            "data-product-key": "jiraSoftware",
            "data-event-component": "linkButton",
            "data-event-container": "announcementBanner",
            "data-event": "clicked",
            "data-label": "Learn more",
            "data-label-english": "Learn more"
        }
    };

    const showToast = FeatureGates.checkGate(
        "gwp-atlassian-recommends-jsw-exp"
    );
    const showTeamBanner = FeatureGates.getExperimentValue(
        "wac-fy24-team-24-banner-jira",
        "ShowButterBar",
        false
    );
    const teamBannerMessages: BannerProps = {
        identifier: "jira-software.banner-alert.team-24",
        text: intl.formatMessage({
            id: "jira-software.banner-alert.text.team-24",
            defaultMessage:
                "Our annual conference is back in Las Vegas! Be the first to see our latest innovations. For a limited time only, save $1000 on your all-access pass."
        }),
        linkText: intl.formatMessage({
            id: "jira-software.banner-alert.team-24.link-text",
            defaultMessage: "Register now"
        }),
        linkUrl:
            "https://events.atlassian.com/team?ref=Team_24&utm_source=marketing&utm_campaign=acquisition&utm_content=outreach&utm_medium=jira_banner",
        dataEventProps: {
            "data-product-key": "jiraSoftware",
            "data-event-component": "linkButton",
            "data-event-container": "announcementBanner",
            "data-event": "clicked",
            "data-label": "Reigster now",
            "data-label-english": "Register now"
        }
    };

    // ATLAS-53087: Show welcome message and product link to logged in users with available Jira sites
    const HeroSection =
        userProfile && availableJiraSites.length > 0 ? (
            <LoggedInHeroSection
                availableJiraSites={availableJiraSites}
                userProfileName={userProfile?.name}
            />
        ) : (
            <LoggedOutHeroSection />
        );

    // ATLAS-58441: Show Team 24 banner for logged in users
    let showTeam24BannerSection = false;
    if (userProfile && availableJiraSites.length > 0) {
        showTeam24BannerSection = FeatureGates.getExperimentValue(
            "jsw_team_24_ad_banner",
            "showAdBanner",
            false
        );
    }

    return (
        <div>
            <div>
                <GlobalNavigation
                    showTryNow={false}
                    color={"#f4f5f7"}
                    productKey="jiraSoftware"
                />
            </div>
            <ParentScrollTracking scrollTrackingEnabled={true} pageId="jsw">
                <JiraSoftwareNav />
                {showAiBanner ? (
                    <BannerAlert {...aiBannerMessages} />
                ) : showTeamBanner ? (
                    <BannerAlert {...teamBannerMessages} />
                ) : null}
                {HeroSection}
                {showTeam24BannerSection && <Team24AdBannerSection />}
                <TabsSection />
                <WorkflowSection />
                <IntegrationSection />
                <DevOpsSection />
                <BlogsSection />
                <ScalingSection />
                <CTASection />
                {showToast && (
                    <ToastComponent
                        title="Does a move to Cloud feel too complex?"
                        description="Get a recommendation from a Cloud Specialized Solution Partner."
                        cta={{
                            text: "Learn more",
                            url: "/roadmap/cloud"
                        }}
                        product="home"
                    />
                )}

                <GlobalFooter />
            </ParentScrollTracking>
        </div>
    );
};
